import { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { isEmailAuthEnabled } from '@/auth/config';
import { isBlockedEmailDomain, BLOCKED_EMAIL_MESSAGE } from '@/auth/blockedEmailDomains';
import { setStoredEmailToken } from '@/auth/emailAuth';
import { useAuth } from '@/auth/useAuth';
import { apiPostNoAuth } from '@/api/client';
import logoUrl from '@/assets/silisense-logo.png';
import Turnstile, { isCaptchaEnabled } from '@/components/Turnstile';

/**
 * Sign up with company email only (no Gmail, Yahoo, etc.).
 * Shown only when VITE_EMAIL_AUTH_ENABLED=true.
 */
export default function Signup() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (isBlockedEmailDomain(trimmed)) {
      setError(BLOCKED_EMAIL_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (isCaptchaEnabled() && !captchaToken) {
      setError('Please complete the CAPTCHA challenge.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiPostNoAuth<{ accessToken?: string }>('/v1/auth/register', {
        email: trimmed,
        password,
        captchaToken,
      });
      if (res?.accessToken) {
        setStoredEmailToken(res.accessToken);
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
        return;
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      const msg =
        raw === 'Failed to fetch' || raw.toLowerCase().includes('fetch')
          ? 'Cannot reach the server. Set VITE_API_BASE_URL in config.env to your Azure API URL and redeploy the webapp, or sign in with Microsoft SSO from the login page.'
          : raw;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (!isEmailAuthEnabled()) return <Navigate to="/login" replace />;

  return (
    <div className="login-page">
      <div className="login-card">
        <img
          src={logoUrl}
          alt="SILISENSE — Academic Integrity Reimagined"
          className="login-logo"
          width={280}
          height={90}
          decoding="async"
        />
        <p className="login-subtitle">Create an account</p>
        <p className="login-domain">silisense.digital</p>

        {error && (
          <div className="login-error" role="alert">
            <span className="login-error-text">{error}</span>
            <button
              type="button"
              className="login-error-dismiss"
              onClick={() => setError('')}
              aria-label="Dismiss"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="signup-email">Work email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="form-input"
              disabled={loading}
            />
            <p className="form-hint">Company or institution email only. No Gmail, Yahoo, Outlook, etc.</p>
          </div>
          <div className="form-group">
            <label htmlFor="signup-password">Password</label>
            <div className="password-input-wrap">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                minLength={8}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="signup-confirm">Confirm password</label>
            <div className="password-input-wrap">
              <input
                id="signup-confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                minLength={8}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {isCaptchaEnabled() ? (
            <Turnstile onToken={setCaptchaToken} />
          ) : null}
          <button type="submit" className="btn-primary btn-login" disabled={loading}>
            {loading ? 'Creating account…' : 'Sign up'}
          </button>
        </form>

        <p className="login-hint">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
